/**
 * Product Gallery with PhotoSwipe
 * Accessible image carousel with lightbox for product pages
 */

window.ProductGalleryPhotoSwipe = window.ProductGalleryPhotoSwipe || class ProductGalleryPhotoSwipe {
  constructor(container) {
    this.container = container;
    this.carouselElement = container.querySelector('[data-photoswipe-carousel]');
    this.thumbnails = container.querySelectorAll('[data-thumbnail-index]');
    this.currentIndex = 0;
    this.items = [];
    
    // Zoom state
    this.zoomLevel = 1;
    this.maxZoom = 3;
    this.minZoom = 1;
    this.isPanning = false;
    this.startX = 0;
    this.startY = 0;
    this.translateX = 0;
    this.translateY = 0;
    if (this.carouselElement) {
      this.init();
    }
  }

  init() {
    // Build items array
    this.buildItems();
    
    // Setup carousel navigation
    this.setupCarousel();
    
    // Setup thumbnail navigation
    this.setupThumbnails();
    
    // Handle media playback
    this.setupMediaPlayback();
    
    // Setup zoom modal
    this.setupZoomModal();
    
    // Update on variant changes
    this.observeVariantChanges();
    
    // Set initial slide
    this.goToSlide(this.getInitialIndex());
  }

  buildItems() {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item');
    this.items = Array.from(slides).map((slide, index) => {
      const img = slide.querySelector('img');
      const mediaType = slide.dataset.mediaType;
      
      if (mediaType === 'image' && img) {
        return {
          src: img.dataset.zoom || img.src,
          w: parseInt(img.dataset.zoomWidth) || 1800,
          h: parseInt(img.dataset.zoomWidth) || 1800,
          msrc: img.src,
          alt: img.alt || '',
          element: slide,
          mediaType: 'image'
        };
      }
      
      return {
        html: slide.innerHTML,
        element: slide,
        mediaType: mediaType
      };
    });
  }

  getInitialIndex() {
    const initialMediaId = this.carouselElement.dataset.initialMediaId;
    if (!initialMediaId) return 0;
    
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item');
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].dataset.mediaId === initialMediaId) {
        return i;
      }
    }
    return 0;
  }

  setupCarousel() {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item');
    
    // Hide all slides except current
    slides.forEach((slide, index) => {
      if (index !== this.currentIndex) {
        slide.style.display = 'none';
        slide.classList.remove('is-selected');
      } else {
        slide.classList.add('is-selected');
      }
    });

    // Add navigation buttons if more than one slide
    if (slides.length > 1) {
      this.addNavigationButtons();
      this.addKeyboardNavigation();
      this.addSwipeNavigation();
    }
  }

  addNavigationButtons() {
    const wrapper = this.carouselElement.closest('.product-gallery__carousel-wrapper');
    
    // Previous button
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'product-gallery__nav product-gallery__nav--prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    prevBtn.addEventListener('click', () => this.previousSlide());
    
    // Next button
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'product-gallery__nav product-gallery__nav--next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    nextBtn.addEventListener('click', () => this.nextSlide());
    
    wrapper.appendChild(prevBtn);
    wrapper.appendChild(nextBtn);
    
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;
    
    this.updateNavigationButtons();
  }

  addKeyboardNavigation() {
    this.carouselElement.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previousSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });
  }

  addSwipeNavigation() {
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    
    this.carouselElement.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    });
    
    this.carouselElement.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;
      
      // Only handle horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault();
      }
    });
    
    this.carouselElement.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      
      // Only trigger if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          this.previousSlide();
        } else {
          this.nextSlide();
        }
      }
      
      isDragging = false;
    });
  }



  setupThumbnails() {
    if (!this.thumbnails.length) return;

    // Double-tap detection variables
    let lastTapTime = 0;

    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        const slideIndex = parseInt(thumbnail.dataset.thumbnailIndex);
        this.goToSlide(slideIndex);
      });

      // Keyboard navigation for thumbnails
      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const slideIndex = parseInt(thumbnail.dataset.thumbnailIndex);
          this.goToSlide(slideIndex);
        }
      });

      thumbnail.addEventListener('dblclick', (event) => {
        event.preventDefault();
        // Open zoom modal for this slide
        const modal = document.getElementById('zoom-modal');
        if (modal) {
          this.goToSlide(index, false); // Go to the correct slide without announcing
          this.openZoomModal(modal);
        }
      });

      // Double-tap for mobile
      thumbnail.addEventListener('touchend', (event) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapTime;
        if (tapLength < 300 && tapLength > 0) {
          event.preventDefault();
          const modal = document.getElementById('zoom-modal');
          if (modal) {
            this.goToSlide(index, false);
            this.openZoomModal(modal);
          }
        }
        lastTapTime = currentTime;
      });

    });

    // Update active thumbnail on initial load
    this.updateActiveThumbnail(this.currentIndex);
  }

  goToSlide(index, announce = true) {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    
    if (index < 0 || index >= slides.length) return;
    
    // Pause current media
    this.pauseAllMedia();
    
    // Hide all slides
    slides.forEach((slide, i) => {
      if (i === index) {
        slide.style.display = 'block';
        slide.classList.add('is-selected');
        slide.setAttribute('aria-hidden', 'false');
        slide.setAttribute('aria-current', 'true');
        const img = slide.querySelector('img');
        if (img) img.setAttribute('aria-current', 'true');
        if (slide.dataset.mediaType === 'image') {
          setTimeout(() => { slide.focus(); }, 100);
        }
      } else {
        slide.style.display = 'none';
        slide.classList.remove('is-selected');
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('aria-current');
        const img = slide.querySelector('img');
        if (img) img.removeAttribute('aria-current');
      }
    });
    
    this.currentIndex = index;
    this.updateActiveThumbnail(index);
    this.updateNavigationButtons();
    
    if (announce) {
      this.announceSlideChange(index);
    }
    
    this.scrollThumbnailIntoView(index);
  }

  previousSlide() {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : slides.length - 1;
    this.goToSlide(newIndex);
  }

  nextSlide() {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    const newIndex = this.currentIndex < slides.length - 1 ? this.currentIndex + 1 : 0;
    this.goToSlide(newIndex);
  }

  updateNavigationButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    
    // Always enable for wrap-around
    this.prevBtn.disabled = false;
    this.nextBtn.disabled = false;
  }

  setupMediaPlayback() {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item');
    
    slides.forEach((slide, index) => {
      const mediaType = slide.dataset.mediaType;
      
      if (mediaType === 'video' || mediaType === 'external_video') {
        const video = slide.querySelector('video, iframe');
        if (video) {
          // Auto-pause when slide changes
          slide.addEventListener('transitionend', () => {
            if (this.currentIndex !== index) {
              this.pauseMedia(video);
            }
          });
        }
      }
    });
  }

  pauseAllMedia() {
    const allVideos = this.carouselElement.querySelectorAll('video, iframe');
    allVideos.forEach(video => this.pauseMedia(video));
  }

  pauseMedia(video) {
    if (video.tagName === 'VIDEO') {
      video.pause();
    } else if (video.tagName === 'IFRAME') {
      video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    }
  }

  updateActiveThumbnail(index) {
    this.thumbnails.forEach((thumbnail, i) => {
      if (i === index) {
        thumbnail.classList.add('is-nav-selected');
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.classList.remove('is-nav-selected');
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  scrollThumbnailIntoView(index) {
    const thumbnail = this.thumbnails[index];
    if (thumbnail) {
      thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  announceSlideChange(index) {
    const totalSlides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)').length;
    const announcement = `Image ${index + 1} of ${totalSlides}`;
    
    // Create or update live region
    let liveRegion = this.container.querySelector('.product-gallery__live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden product-gallery__live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      this.container.appendChild(liveRegion);
    }
    
    liveRegion.textContent = announcement;
  }

  setupZoomModal() {
    const modal = document.getElementById('zoom-modal');
    if (!modal) return;
    // Use event delegation so DOM restructuring (e.g. Flickity) doesn't break the listener
    this.container.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-gallery__zoom-notice');
      if (!btn) return;
      e.preventDefault();
      this.openZoomModal(modal);
    });

    // Close modal when clicking close button
    const closeButton = modal.querySelector('.pswp__button--close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeZoomModal(modal);
      });
    }
    
    // Close modal when clicking background
    const background = modal.querySelector('.pswp__bg');
    if (background) {
      background.addEventListener('click', () => {
        this.closeZoomModal(modal);
      });
    }
    
    // Handle navigation buttons
    const prevButton = modal.querySelector('.pswp__button--arrow--left');
    const nextButton = modal.querySelector('.pswp__button--arrow--right');
    
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.navigateZoomModal('prev');
      });
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.navigateZoomModal('next');
      });
    }
    
    // Handle zoom button
    const zoomInButton = modal.querySelector('.pswp__button--zoom');
    if (zoomInButton) {
      zoomInButton.addEventListener('click', () => {
        const img = modal.querySelector('.pswp__img');
        if (img) {
          // Zoom to 200% (2x)
          this.zoom(img, 2);
        }
      });
    }
    
    // Handle keyboard events
    this.handleModalKeydown = (e) => {
      if (!modal.classList.contains('pswp--open')) return;
      
      if (e.key === 'Escape') {
        this.closeZoomModal(modal);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateZoomModal('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateZoomModal('next');
      } else if (e.key === 'Tab') {
        this.trapFocus(e, modal);
      }
    };
    
    document.addEventListener('keydown', this.handleModalKeydown);
  }

  openZoomModal(modal) {
    // Store the element that had focus before opening modal
    this.previouslyFocusedElement = document.activeElement;
    
    // Add open class to show modal
    modal.classList.add('pswp--open');
    document.body.style.overflow = 'hidden';
    
    // Hide other content from screen readers on mobile
    if (this.isMobile()) {
      this.hideBodyContentForScreenReaders(modal);
    }
    
    // Load the current image into the modal
    this.updateZoomModalContent(modal);
    
    // Focus the close button for accessibility
    const closeButton = modal.querySelector('.pswp__button--close');
    if (closeButton) {
      setTimeout(() => closeButton.focus(), 100);
    }
  }

  closeZoomModal(modal) {
    modal.classList.remove('pswp--open');
    document.body.style.overflow = '';
    
    // Restore visibility for screen readers on mobile
    if (this.isMobile()) {
      this.restoreBodyContentForScreenReaders();
    }
    
    // Return focus to the element that opened the modal
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  isMobile() {
    return window.innerWidth <= 768;
  }

  hideBodyContentForScreenReaders(modal) {
    // Store elements that already have aria-hidden="true"
    this.elementsWithAriaHidden = [];
    console.log('Hiding body content for screen readers');
    // Get all direct children of body
    const bodyChildren = Array.from(document.body.children);
    
    bodyChildren.forEach(element => {
      // Skip the modal itself
      if (element === modal || element.contains(modal)) {
        return;
      }
      
      // Check if element already has aria-hidden="true"
      const hasAriaHidden = element.getAttribute('aria-hidden') === 'true';
      
      if (!hasAriaHidden) {
        // Add both aria-hidden and inert for complete blocking
        // inert prevents other scripts from interfering
        element.setAttribute('aria-hidden', 'true');
        element.inert = true;
        this.elementsWithAriaHidden.push(element);
      }
    });
  }

  restoreBodyContentForScreenReaders() {
    // Remove aria-hidden and inert from elements we added them to
    if (this.elementsWithAriaHidden) {
      this.elementsWithAriaHidden.forEach(element => {
        element.removeAttribute('aria-hidden');
        element.inert = false;
      });
      
      // Clear the array
      this.elementsWithAriaHidden = [];
    }
  }

  trapFocus(e, modal) {
    // Get all focusable elements within the modal
    const focusableElements = modal.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const focusableArray = Array.from(focusableElements);
    const firstFocusable = focusableArray[0];
    const lastFocusable = focusableArray[focusableArray.length - 1];
    
    // If Shift + Tab on first element, go to last
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } 
    // If Tab on last element, go to first
    else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  updateZoomModalContent(modal) {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    const currentSlide = slides[this.currentIndex];
    
    if (!currentSlide) return;
    
    const img = currentSlide.querySelector('img');
    if (!img) return;
    
    // Reset zoom state
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    
    // Update pagination
    const paginationCurrent = modal.querySelector('.pswp__pagination-current');
    const paginationCount = modal.querySelector('.pswp__pagination-count');
    
    if (paginationCurrent && paginationCount) {
      paginationCurrent.textContent = this.currentIndex + 1;
      paginationCount.textContent = slides.length;
    }
    
    // Load the high-res image into the modal
    const container = modal.querySelector('.pswp__container');
    const item = container.querySelector('.pswp__item');
    
    if (item) {
      // Clear previous content
      item.innerHTML = '';
      
      // Create zoom wrap
      const zoomWrap = document.createElement('div');
      zoomWrap.className = 'pswp__zoom-wrap';
      
      // Create image
      const zoomedImg = document.createElement('img');
      zoomedImg.className = 'pswp__img';
      zoomedImg.src = img.dataset.zoom || img.src;
      zoomedImg.alt = img.alt || '';
      
      // Add zoom event listeners
      this.setupZoomEvents(zoomedImg, zoomWrap);
      
      zoomWrap.appendChild(zoomedImg);
      item.appendChild(zoomWrap);
    }
  }
  setupZoomEvents(img, zoomWrap) {
    let lastTap = 0;
    
    // Click to zoom in/out
    img.addEventListener('click', (e) => {
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTap;
      
      // Double-tap detection
      if (tapLength < 300 && tapLength > 0) {
        e.preventDefault();
        this.toggleZoom(img, e);
      }
      lastTap = currentTime;
    });
    
    // Mouse wheel zoom
    zoomWrap.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom(img, this.zoomLevel + delta, e.clientX, e.clientY);
    }, { passive: false });
    
    // Touch events for pinch zoom
    let initialDistance = 0;
    let initialZoom = 1;
    
    zoomWrap.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance = this.getDistance(e.touches[0], e.touches[1]);
        initialZoom = this.zoomLevel;
      } else if (e.touches.length === 1 && this.zoomLevel > 1) {
        this.isPanning = true;
        this.startX = e.touches[0].clientX - this.translateX;
        this.startY = e.touches[0].clientY - this.translateY;
      }
    }, { passive: false });
    
    zoomWrap.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / initialDistance;
        this.zoom(img, initialZoom * scale);
      } else if (e.touches.length === 1 && this.isPanning) {
        e.preventDefault();
        this.translateX = e.touches[0].clientX - this.startX;
        this.translateY = e.touches[0].clientY - this.startY;
        this.applyTransform(img);
      }
    }, { passive: false });
    
    zoomWrap.addEventListener('touchend', () => {
      this.isPanning = false;
    });
    
    // Mouse drag for panning when zoomed
    zoomWrap.addEventListener('mousedown', (e) => {
      if (this.zoomLevel > 1) {
        e.preventDefault();
        this.isPanning = true;
        this.startX = e.clientX - this.translateX;
        this.startY = e.clientY - this.translateY;
        zoomWrap.style.cursor = 'grabbing';
      }
    });
    
    document.addEventListener('mousemove', (e) => {
      if (this.isPanning) {
        this.translateX = e.clientX - this.startX;
        this.translateY = e.clientY - this.startY;
        this.applyTransform(img);
      }
    });
    
    document.addEventListener('mouseup', () => {
      if (this.isPanning) {
        this.isPanning = false;
        zoomWrap.style.cursor = this.zoomLevel > 1 ? 'grab' : 'zoom-in';
      }
    });
  }
  
  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  toggleZoom(img, event) {
    if (this.zoomLevel > 1) {
      this.zoom(img, 1);
    } else {
      const rect = img.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      this.zoom(img, 2, x, y);
    }
  }
  
  zoom(img, newZoom, centerX, centerY) {
    const oldZoom = this.zoomLevel;
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
    
    // Adjust pan to zoom toward the cursor/touch point
    if (centerX !== undefined && centerY !== undefined) {
      const rect = img.getBoundingClientRect();
      const offsetX = centerX - rect.width / 2;
      const offsetY = centerY - rect.height / 2;
      
      this.translateX -= offsetX * (this.zoomLevel - oldZoom);
      this.translateY -= offsetY * (this.zoomLevel - oldZoom);
    }
    
    // Reset pan when zooming back to 1x
    if (this.zoomLevel === 1) {
      this.translateX = 0;
      this.translateY = 0;
    }
    
    this.applyTransform(img);
    
    // Update cursor
    const zoomWrap = img.parentElement;
    if (zoomWrap) {
      zoomWrap.style.cursor = this.zoomLevel > 1 ? 'grab' : 'zoom-in';
    }
  }
  
  applyTransform(img) {
    img.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoomLevel})`;
    img.style.transition = this.isPanning ? 'none' : 'transform 0.3s ease';
  }
  navigateZoomModal(direction) {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)');
    
    if (direction === 'prev') {
      this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : slides.length - 1;
    } else {
      this.currentIndex = this.currentIndex < slides.length - 1 ? this.currentIndex + 1 : 0;
    }
    
    const modal = document.getElementById('zoom-modal');
    if (modal) {
      this.updateZoomModalContent(modal);
    }
  }

  observeVariantChanges() {
    // Watch for variant changes and update gallery
    const productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.addEventListener('variant-change', (e) => {
        if (e.detail && e.detail.variant && e.detail.variant.featured_media) {
          this.selectMediaById(e.detail.variant.featured_media.id);
        }
      });
    }
  }

  selectMediaById(mediaId) {
    const slides = this.carouselElement.querySelectorAll('.product-gallery__carousel-item');
    slides.forEach((slide, index) => {
      if (slide.dataset.mediaId === mediaId.toString()) {
        this.goToSlide(index);
      }
    });
  }




};

// Initialize on DOM ready; guard prevents double-init when snippet renders twice
if (!window._productGalleryPhotoSwipeInit) {
  window._productGalleryPhotoSwipeInit = true;
  function initGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
      new ProductGalleryPhotoSwipe(gallery);
    });
  }

  // Handle case where DOMContentLoaded already fired (common with defer scripts)
  if (document.readyState !== 'loading') {
    initGalleries();
  } else {
    document.addEventListener('DOMContentLoaded', initGalleries);
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const gallery = event.target.querySelector('[data-product-gallery]');
      if (gallery) {
        new ProductGalleryPhotoSwipe(gallery);
      }
    });
  }
}